import { useState, useEffect } from 'react'
import BFTable from './small/BFTable'
import { useCryptosByMarketCap } from '../endpoints/index'
import BFLoading from './small/BFLoading'
import { Link } from 'react-router-dom'

export default function TopMarketCapCryptos({ title = 'Top Cryptocurrencies by Market Cap' }) {
	const [sortField, setSortField] = useState('market_cap')
	const [sortOrder, setSortOrder] = useState('desc')
	const [currentPage, setCurrentPage] = useState(0)
	const [allCryptos, setAllCryptos] = useState([])
	const [isLoadingMore, setIsLoadingMore] = useState(false)

	// Determine if we're on the coins page or home page
	const isCoinsPage = window.location.pathname === '/coins'
	const limit = 10 // Set to 10 cryptos per page

	// Fetch data for current page
	const { data, isLoading, isError } = useCryptosByMarketCap(sortField, sortOrder, currentPage, limit)

	// Debug logging
	useEffect(() => {
		console.log('Hook state:', { data: data?.length, isLoading, isError, currentPage })
	}, [data, isLoading, isError, currentPage])

	// Reset data when sort changes
	useEffect(() => {
		console.log('Sort changed, resetting data')
		setAllCryptos([])
		setCurrentPage(0)
		setIsLoadingMore(false)
	}, [sortField, sortOrder])

	// Function to clean crypto data
	const cleanCryptoData = (cryptos) => {
		return cryptos.map(crypto => {
			const cleaned = { ...crypto };
			// Replace NaN, Infinity, and -Infinity with null or 0
			Object.keys(cleaned).forEach(key => {
				if (typeof cleaned[key] === 'number') {
					if (isNaN(cleaned[key]) || !isFinite(cleaned[key])) {
						// For price-related fields, use 0, for others use null
						if (key.includes('price') || key.includes('value') || key.includes('cap')) {
							cleaned[key] = 0;
						} else {
							cleaned[key] = null;
						}
					}
				}
			});
			return cleaned;
		});
	};

	// Handle new data coming in
	useEffect(() => {
		console.log('Data effect triggered:', {
			dataLength: data?.length,
			currentPage,
			isLoading,
			isLoadingMore
		})

		if (data && data.length > 0 && !isLoading) {
			// Clean the data to handle NaN values
			const cleanedData = cleanCryptoData(data);

			if (currentPage === 0) {
				// First page - replace all data
				console.log('Setting initial data:', cleanedData.length)
				setAllCryptos(cleanedData)
			} else {
				// Subsequent pages - append data
				console.log('Appending data:', cleanedData.length)
				setAllCryptos(prev => {
					const newData = [...prev, ...cleanedData]
					console.log('Total cryptos after append:', newData.length)
					return newData
				})
			}
			setIsLoadingMore(false)
		}
	}, [data, currentPage, isLoading])

	const sortBy = (label) => {
		console.log('Sorting by:', label)
		if (label === sortField) {
			setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')
		} else {
			setSortField(label)
			setSortOrder('desc')
		}
	}

	const loadNextPage = () => {
		console.log('Load next page clicked, current conditions:', {
			isLoading,
			isLoadingMore,
			currentPage
		})

		if (!isLoading && !isLoadingMore) {
			console.log('Loading next page:', currentPage + 1)
			setIsLoadingMore(true)
			setCurrentPage(prev => prev + 1)
		}
	}

	// Show error state
	if (isError) {
		return (
			<div className="bg-main-white min-w-full overflow-y-auto p-4 space-y-4 rounded-2xl">
				<h1 className="text-[20px] mb-6 font-DM_Sans font-medium leading-normal tracking-wide text-main-black">
					Top Cryptocurrencies by Marketcap
				</h1>
				<div className="text-center py-8 text-red-500">
					Error loading cryptocurrencies. Please try again.
				</div>
			</div>
		)
	}

	// Show loading for initial load
	if (isLoading && currentPage === 0 && allCryptos.length === 0) {
		return (
			<div className="bg-main-white min-w-full overflow-y-auto p-4 space-y-4 rounded-2xl">
				<h1 className="text-[20px] mb-6 font-DM_Sans font-medium leading-normal tracking-wide text-main-black">
					Top Cryptocurrencies by Marketcap
				</h1>
				<div className="h-96 bg-white">
					<BFLoading heightAdjust="h-[45vh]" />
				</div>
			</div>
		)
	}

	return (
		<div className="bg-main-white min-w-full overflow-y-auto p-4 space-y-4 rounded-2xl">
			<h1 className="text-[20px] mb-6 font-DM_Sans font-medium leading-normal tracking-wide text-main-black">
				Top Cryptocurrencies by Marketcap
			</h1>

			{/* Debug info - remove in production */}
			{process.env.NODE_ENV === 'development' && (
				<div className="text-xs text-gray-500 p-2 bg-gray-100 rounded">
					Debug: Page {currentPage}, Total: {allCryptos.length}, Loading: {isLoading ? 'yes' : 'no'}, LoadingMore: {isLoadingMore ? 'yes' : 'no'}
				</div>
			)}

			<BFTable
				showHeader={true}
				condensedHeaders={true}
				rows={allCryptos}
				type="top-cryptos"
				handleHeaderClick={sortBy}
			/>

			{/* Loading indicator for additional pages */}
			{isLoadingMore && (
				<div className="text-center py-4">
					<BFLoading heightAdjust="h-[100px]" />
				</div>
			)}

			{/* Buttons */}
			{isCoinsPage ? (
				// Load More button for coins page
				<div className="text-center mt-2">
					<button
						onClick={loadNextPage}
						disabled={isLoadingMore || isLoading}
						className={`relative text-[15px] text-white py-3 rounded-lg bg-main-buttonBlue text-center shadow-sm shadow-main-shadowBlue font-DM_Sans font-medium leading-normal tracking-wide w-full sm:w-[300px] ${
							(isLoadingMore || isLoading) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
						}`}
					>
						{isLoadingMore ? 'Loading...' : 'Load more'}
					</button>
				</div>
			) : (
				// "See all" button for home page with fade effect
				<div className="text-center mt-2 pb-6">
					<div className="static h-24 w-full bg-gradient-to-t from-white -mb-14 -mt-28 opacity-90"></div>
					<div className="static h-24 w-full bg-gradient-to-t from-white -mb-12 -mt-28 opacity-30"></div>
					<div className="static h-24 w-full bg-gradient-to-t from-white -mb-6 -mt-28 opacity-80 md:hidden"></div>
					<div className='flex justify-center w-full -mb-1'>
						<Link
							to="/coins"
							className="relative text-[15px] text-white py-3 rounded-lg bg-main-buttonBlue text-center shadow-sm shadow-main-shadowBlue font-DM_Sans font-medium leading-normal tracking-wide w-full sm:w-[300px]"
						>
							See all Cryptocurrencies
						</Link>
					</div>
				</div>
			)}
		</div>
	)
}
